import { ChangeEvent, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { getEngAdminAutocomplete } from "../../redux/actions/fluxoNSimilar";
import Input from "../fluxo-engenheiro/formBasic/Input";
import { ModalButtons } from "../ModalButtons/modalButtons";
import ContainerError from "../error/containerError";
import IconAlert from "../../assets/svgs/icon-alert.svg";
import "../fluxo-tax/smallModalEdit/smallModal.css";
import "./modalNovoGrupo.css";

interface UserAutocompleteOption {
  id: string;
  email: string;
}

interface CurrentAdmin {
  id: string;
  name: string;
}

interface EditAdminModalContent {
  title?: string;
  currentAdminLabel?: string;
  transferWarning?: string;
}

interface ModalNovoGrupoProps {
  mode?: "create" | "edit-admin";
  groupName?: string;
  currentAdmin?: CurrentAdmin;
  editAdminContent?: EditAdminModalContent;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (data: { name?: string; engineerResponsibleId: string }) => void;
}

const MAX_GROUP_NAME_LENGTH = 30;

const getInitials = (fullName: string) =>
  fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const ModalNovoGrupo = ({
  mode = "create",
  groupName,
  currentAdmin,
  editAdminContent,
  isSubmitting,
  onClose,
  onConfirm,
}: ModalNovoGrupoProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const isEditAdmin = mode === "edit-admin";
  const [name, setName] = useState("");
  const [administratorSearch, setAdministratorSearch] = useState("");
  const [administratorOptions, setAdministratorOptions] = useState<
    UserAutocompleteOption[]
  >([]);
  const [selectedAdministrator, setSelectedAdministrator] =
    useState<UserAutocompleteOption | null>(null);
  const [showAdministratorDropdown, setShowAdministratorDropdown] =
    useState(false);
  const [touchedFields, setTouchedFields] = useState({
    name: false,
    administrator: false,
  });

  const handleAdministratorChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setAdministratorSearch(value);
    setSelectedAdministrator(null);

    if (value.length < 3) {
      setAdministratorOptions([]);
      setShowAdministratorDropdown(false);
      return;
    }

    try {
      const response = await dispatch(
        getEngAdminAutocomplete({ search: value }),
      ).unwrap();
      const options = response as UserAutocompleteOption[];

      setAdministratorOptions(options);
      setShowAdministratorDropdown(options.length > 0);
    } catch {
      setAdministratorOptions([]);
      setShowAdministratorDropdown(false);
    }
  };

  const handleSelectAdministrator = (option: UserAutocompleteOption) => {
    setSelectedAdministrator(option);
    setAdministratorSearch(option.email);
    setShowAdministratorDropdown(false);
  };

  const trimmedName = name.trim();
  const isConfirmDisabled =
    (!isEditAdmin &&
      (!trimmedName || trimmedName.length > MAX_GROUP_NAME_LENGTH)) ||
    !selectedAdministrator ||
    isSubmitting;
  const showNameError = !isEditAdmin && touchedFields.name && !trimmedName;
  const showNameLengthError =
    !isEditAdmin && trimmedName.length > MAX_GROUP_NAME_LENGTH;
  const showAdministratorError =
    touchedFields.administrator && !selectedAdministrator;
  const editAdminTitle =
    editAdminContent?.title ?? "Alterar Administrador de Engenharia";
  const currentAdminLabel =
    editAdminContent?.currentAdminLabel ?? "ADMINISTRADOR ATUAL";

  return (
    <div
      className="smallModalCorporateSession groupModal"
      data-testid={isEditAdmin ? "edit-admin-modal" : "new-group-modal"}
    >
      <div className="smallModal">
        <div className="containerTitleSmallModal groupModalTitle">
          <h1 className="titleSmallModal">
            {isEditAdmin
              ? editAdminTitle
              : "Novo Grupo de Responsabilidade"}
          </h1>
          {isEditAdmin && groupName && (
            <span className="groupModalSubtitle">{groupName}</span>
          )}
        </div>

        <div className="containerDescription">
          {!isEditAdmin && (
            <div className="inputRow">
              <span style={{ marginTop: 15 }}>Nome do Grupo</span>
              <div>
                <Input
                  label=""
                  width="100%"
                  placeholder="Ex: Grupo PT - Centro 6845"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  onBlur={() =>
                    setTouchedFields((current) => ({ ...current, name: true }))
                  }
                  data-testid="input-group-name"
                />
                {showNameError && (
                  <ContainerError
                    id="group-name-error"
                    label="Campo obrigatório."
                    icon={IconAlert}
                    widthError="100%"
                    backgroudError="#FFD9D9"
                    colorError="#000000"
                  />
                )}
                {showNameLengthError && (
                  <ContainerError
                    id="group-name-length-error"
                    label="O limite é de 30 caracteres."
                    icon={IconAlert}
                    widthError="100%"
                    backgroudError="#FFD9D9"
                    colorError="#000000"
                  />
                )}
              </div>
            </div>
          )}

          {isEditAdmin && currentAdmin && (
            <div className="currentAdminCard" data-testid="current-admin-card">
              <div className="currentAdminAvatar">
                {getInitials(currentAdmin.name)}
              </div>
              <div className="currentAdminInfo">
                <span className="currentAdminLabel">{currentAdminLabel}</span>
                <span className="currentAdminName">{currentAdmin.name}</span>
              </div>
            </div>
          )}

          <div className="inputRow">
            <span style={{ marginTop: 15 }}>
              {isEditAdmin
                ? "Novo Administrador de Engenharia"
                : "Administrador de Engenharia"}
            </span>
            <div style={{ position: "relative" }}>
              <div>
                <Input
                  label=""
                  width="100%"
                  placeholder="Buscar usuário por email..."
                  value={administratorSearch}
                  onChange={handleAdministratorChange}
                  onBlur={() =>
                    setTouchedFields((current) => ({
                      ...current,
                      administrator: true,
                    }))
                  }
                  data-testid="input-group-administrator"
                />

                {showAdministratorDropdown &&
                  administratorOptions.length > 0 && (
                    <div
                      className="dataResult aprovadorDropdown"
                      data-testid="administrator-dropdown"
                    >
                      {administratorOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className="dataItem"
                          aria-label={`Selecionar administrador ${option.email}`}
                          data-testid={`administrator-option-${option.id}`}
                          onClick={() => handleSelectAdministrator(option)}
                          onMouseDown={(event) => event.preventDefault()}
                        >
                          {option.email}
                        </button>
                      ))}
                    </div>
                  )}
              </div>
              {showAdministratorError && (
                <ContainerError
                  id="group-administrator-error"
                  label="Campo obrigatório."
                  icon={IconAlert}
                  widthError="100%"
                  backgroudError="#FFD9D9"
                  colorError="#000000"
                />
              )}
              {editAdminContent?.transferWarning && (
                <p className="groupTransferWarning" role="alert">
                  {editAdminContent.transferWarning}
                </p>
              )}
            </div>
          </div>

          <ModalButtons
            buttonOne={isEditAdmin ? "Confirmar" : "Criar Grupo"}
            buttonTwo="Cancelar"
            onConfirm={() =>
              selectedAdministrator &&
              onConfirm({
                ...(isEditAdmin ? {} : { name: trimmedName }),
                engineerResponsibleId: selectedAdministrator.id,
              })
            }
            onClose={onClose}
            isButtonDisabled={isConfirmDisabled}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default ModalNovoGrupo;
